@use "../../scss/colors";

@mixin buttonColor($background, $border, $color) {
	background-color: $background;
	border-color: $border;
	color: $color;
}

// base button styles
.button {
	align-items: center;
	border: 0;
	box-sizing: border-box !important;
	cursor: pointer;
	display: flex;
	flex-direction: row;
	font-size: 16px;
	font-weight: 400;
	height: 42px;
	overflow: hidden;
	// *Note 1
	padding: 0 21px;
	position: relative;
	transition: all 350ms ease-in-out;

	// *Note 2
	i:not(i.icon-no-margin) {
		margin-right: 8px;
	}
}

.noSlant {
	&::after {
		content: "";
		width: 100%;
		height: 100%;
		position: absolute;
		left: 0;
		top: 0;
		transition: all 0.35s ease;
	}

	&:hover::after {
		background: rgba(255, 255, 255, 0.3);
	}
}

.slant {
	&::after {
		content: "";
		background: linear-gradient(
			29.5deg,
			transparent 50%,
			rgba(255, 255, 255, 0.3) 50%
		);
		width: 150%;
		height: 150%;
		position: absolute;
		right: 0;
		top: 30%;
		left: 70%;
		transform: translate(-50%, -50%);
		transition: all 0.35s ease;
	}

	&:hover::after {
		width: 200%;
		height: 200%;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	&:focus::after {
		width: 200%;
		height: 200%;
		top: 50%;
		left: 30%;
		transform: translate(-50%, -50%);
	}
}

.button:disabled,
.button[disabled] {
	opacity: 0.3;
	@include buttonColor(
		colors.$nordic_med_grey,
		colors.$nordic_med_grey,
		colors.$nordic_light_grey
	);

	&:hover {
		cursor: not-allowed;
	}

	&::after {
		display: none;
	}

	&:hover::after {
		display: none;
	}
}

// button colors
.destructive {
	@include buttonColor(colors.$nordic_red, colors.$nordic_red, colors.$white);
}

.dark {
	@include buttonColor(
		colors.$nordic_dark_grey,
		colors.$nordic_dark_grey,
		colors.$white
	);
}

.darkBlue {
	@include buttonColor(
		colors.$nordic_blueslate,
		colors.$nordic_blueslate,
		colors.$white
	);
}

.info {
	@include buttonColor(colors.$nordic_lake, colors.$nordic_lake, colors.$white);
}

.medium {
	@include buttonColor(
		colors.$nordic_med_grey,
		colors.$nordic_med_grey,
		colors.$white
	);
}

.light {
	@include buttonColor(
		colors.$nordic_sky,
		colors.$nordic_sky,
		colors.$nordic_dark_grey
	);

	&:hover {
		color: colors.$white;
	}
}

.lighter {
	@include buttonColor(
		colors.$nordic_light_grey,
		colors.$nordic_light_grey,
		colors.$black
	);

	&:hover {
		color: colors.$white;
	}
}

.primary {
	@include buttonColor(colors.$nordic_blue, colors.$nordic_blue, colors.$white);
}

.success {
	@include buttonColor(
		colors.$nordic_grass,
		colors.$nordic_grass,
		colors.$nordic_dark_grey
	);

	&:hover {
		color: colors.$white;
	}
}

.warning {
	@include buttonColor(
		colors.$nordic_yellow,
		colors.$nordic_yellow,
		colors.$nordic_dark_grey
	);

	&:hover {
		color: colors.$nordic_dark_grey;
	}
}

// secondaryButtons
@mixin secondaryDefaults($background, $border, $color) {
	border: 2px solid $border;
	background-color: $background;
	color: $color;
}

@mixin secondaryFocus($outline) {
	outline: 2px solid $outline !important;
	outline-offset: -7px !important;
}

@mixin secondaryHover_secondaryActive($background) {
	background-color: $background;
	color: colors.$white;
}

.secondaryDefault {
	@include secondaryDefaults(
		colors.$white,
		colors.$nordic_lake,
		colors.$nordic_lake
	);

	&::after {
		display: none;
	}

	// *Note 3
	&:focus-visible {
		@include secondaryFocus(colors.$nordic_blueslate);
	}

	&:hover {
		@include secondaryHover_secondaryActive(colors.$nordic_lake);
	}

	&:hover::after {
		display: none;
	}
}

.secondaryDestructive {
	@include secondaryDefaults(
		colors.$white,
		colors.$nordic_red,
		colors.$nordic_red
	);

	&::after {
		display: none;
	}

	// *Note 3
	&:focus-visible {
		@include secondaryFocus(colors.$red_dark);
	}

	&:hover {
		@include secondaryHover_secondaryActive(colors.$nordic_red);
	}

	&:hover::after {
		display: none;
	}
}

.secondaryNeutral {
	@include secondaryDefaults(
		colors.$white,
		colors.$nordic_dark_grey,
		colors.$nordic_dark_grey
	);

	&::after {
		display: none;
	}

	// *Note 3
	&:focus-visible {
		@include secondaryFocus(colors.$black);
	}

	&:hover {
		@include secondaryHover_secondaryActive(colors.$nordic_dark_grey);
	}

	&:hover::after {
		display: none;
	}
}

.secondaryWarning {
	@include secondaryDefaults(
		colors.$white,
		colors.$nordic_yellow,
		colors.$nordic_yellow
	);

	&::after {
		display: none;
	}

	// *Note 3
	&:focus-visible {
		@include secondaryFocus(colors.$black);
	}

	&:hover {
		@include secondaryHover_secondaryActive(colors.$nordic_yellow);
	}

	&:hover::after {
		display: none;
	}
}

// flat buttons
@mixin flatDefaults($background) {
	background: none;
	color: $background;
	border: 2px solid transparent;
}

@mixin flatFocus($background, $border) {
	background-color: $background;
	border: 2px solid $border;
	outline: 3px solid colors.$white !important;
	outline-offset: -5px !important;
}

@mixin flatHover_flatActive($background) {
	background-color: $background;
	border: 2px solid transparent;
	outline: 0 solid transparent !important;
}

.flatDefault {
	@include flatDefaults(colors.$nordic_lake);

	&::after {
		content: "";
	}

	// *Note 3
	&:focus-visible {
		@include flatFocus(rgba(0, 119, 200, 0.1), colors.$nordic_lake);
	}

	&:hover {
		@include flatHover_flatActive(rgba(0, 119, 200, 0.1));
	}

	&:active {
		@include flatHover_flatActive(rgba(0, 119, 200, 0.1));
	}

	&:hover::after {
		background: none;
	}
}

.flatDestructive {
	@include flatDefaults(colors.$nordic_red);

	// *Note 3
	&:focus-visible {
		@include flatFocus(rgba(222, 49, 79, 0.1), colors.$nordic_red);
	}

	&:hover {
		@include flatHover_flatActive(rgba(222, 49, 79, 0.1));
	}

	&:active {
		@include flatHover_flatActive(rgba(222, 49, 79, 0.1));
	}

	&:hover::after {
		background: none;
	}
}

.flatNeutral {
	@include flatDefaults(colors.$nordic_dark_grey);

	// *Note 3
	&:focus-visible {
		@include flatFocus(rgba(51, 63, 72, 0.1), colors.$nordic_dark_grey);
	}

	&:hover {
		@include flatHover_flatActive(rgba(51, 63, 72, 0.1));
	}

	&:active {
		@include flatHover_flatActive(rgba(51, 63, 72, 0.1));
	}

	&:hover::after {
		background: none;
	}
}

.flatDefault:disabled,
.flatDefault[disabled],
.flatDestructive:disabled,
.flatDestructive[disabled],
.flatNeutral:disabled,
.flatNeutral[disabled] {
	opacity: 0.75;
	background: none;
	border: 2px solid transparent;
	color: colors.$grey_text;

	&:hover {
		cursor: not-allowed;
	}

	&::after {
		display: none;
	}

	&:hover::after {
		display: none;
	}
}

// flat buttons
@mixin borderDefault($border, $style, $width, $color) {
	@include flatDefaults(colors.$white);
	border-color: $border;
	border-style: $style;
	border-width: $width;
	color: $color;
}

.borderWhite {
	@include borderDefault(colors.$white, solid, 2px, colors.$white);
}

.borderMedGray {
	@include borderDefault(
		colors.$nordic_med_grey,
		solid,
		2px,
		colors.$nordic_med_grey
	);
}

.centered {
	margin-left: auto;
	margin-right: auto;
}

// button sizes
.large {
	font-size: 18px;
	height: 50px;
	// *Note 1
	padding: 0 25px;

	// *Note 2
	i {
		margin-right: 12px;
	}
}

.small {
	font-size: 16px;
	height: 33px;
	// *Note 1
	padding: 0 16px;

	// *Note 2
	i {
		margin-right: 8px;
	}
}

.marginBottom {
	margin-top: 10px;
}

.marginLeft {
	margin-left: 10px;
}

.marginRight {
	margin-right: 10px;
}

.marginTop {
	margin-top: 10px;
}

.iconButtonLight {
	background-color: transparent;
	padding: 1em;

	i {
		color: colors.$white;
	}

	&:hover {
		i {
			color: colors.$blue_light_hvr;
		}
	}

	&:hover::after {
		background: none;
	}
}

.iconButtonMedium {
	background-color: transparent;
	padding: 1em;

	i {
		color: colors.$grey_text;
	}

	&:hover {
		i {
			color: colors.$blue_light_hvr;
		}
	}

	&:hover::after {
		background: none;
	}
}

.iconButtonDark {
	background-color: transparent;
	padding: 1em;

	i {
		color: colors.$nordic_dark_grey;
	}

	&:hover {
		i {
			color: colors.$nordic_blue;
		}
	}

	&:hover::after {
		background: none;
	}
}

.iconButtonLight:disabled,
.iconButtonLight[disabled],
.iconButtonMedium:disabled,
.iconButtonMedium[disabled],
.iconButtonDark:disabled,
.iconButtonDark[disabled] {
	opacity: 0.25;
	background-color: transparent;
}

.siteIcon {
	margin-right: 0px !important;
	font-size: x-large !important;
}

// *Note 1
// left and right padding should be 1/2 of button height as per
// https://blueprint.nordicsemi.com/305fda22e/p/494d12-button/b/32e1a2

// *Note 2
// Icons on small buttons should have a left margin of 8px.
// Icons on large buttons should have a left margin of 12px.
// https://blueprint.nordicsemi.com/305fda22e/p/494d12-button/b/32e1a2
// icon-no-margin is a utility class for when a button is JUST an icon
// and needs no margin

// *Note 3
// This show focus with keyboard interactions, but doesn't
// show the styles when a user clicks on a button
